import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom';
import moment from 'moment-timezone';

// import { ReactComponent as VectorDown } from '../icons/VectorDown.svg';
import UserAvatar from "../components/UserAvatar";
import { ExpandLess, Close,ExpandMore, FiberManualRecord } from "@mui/icons-material";

import { format, addMonths } from 'date-fns';

import { ResourceBookingSchema, BookingStatusType, FeeSchema, ResourceType, ResourceSchema, DiscountSchema, TrainingInventorySchema, OrderItemSchema, InventorySchema, TeamMemberSchema, MembershipSchema, PlanSchema, ResourcePlanSchema } from "../data/types";
import { useGetPaymentMethods, useLoadMember, useUpdateBooking, useLoadMemberships, useLoadRequests } from '../hooks/memberhooks';
import { capitalizeWords, getBookingStatusColor, getBookingStatusLabel, getBookingStatusOptions, getChangedAttributes, styleThousandCommas, normalizeTeamMembers, getTeamMemberDisplayName, resolveMembershipForTeamMember, planRecurrenceMap, shouldApplyMemberInventoryLimits, getInventoryLimitsForBookingPricing, calculateInventoryCostLinesWithLimits } from '../utils/helpers';
import ReservationCalendar from '../components/ReservationCalendar';
import { ReactComponent as CalendarIcon } from "../icons/CalendarIcon.svg";
import { ReactComponent as RightArrow } from "../icons/ScrollRightArrow.svg";
import { ReactComponent as LeftArrow } from "../icons/ScrollLeftArrow.svg";

import { ReactComponent as VisaCardIcon } from '../icons/visaCardIcon.svg'
// import { ReactComponent as DiscoverCardIcon } from '../../icons/discoverCardIcon.svg'
import { ReactComponent as MasterCardIcon } from '../icons/masterCardIcon.svg'
import { ReactComponent as AmexCardIcon } from '../icons/amexCardIcon.svg'

import { useLoadCurrentResource } from '../hooks/resourcehooks';
import { AddDiscountModal, AddFeeModal, NewMessageModal, MemberDetailsModal } from './AppModal';
import BookingInviteTeamMemberModal, { TeamMemberInviteDetails } from '../components/BookingInviteTeamMemberModal';




type RequestDetailsProps = {
    request: ResourceBookingSchema
    allResourceRequests: ResourceBookingSchema[]
    handleClose: () => void;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ request, allResourceRequests, handleClose }) => {
    // Memoize IDs to prevent unnecessary hook calls when component re-renders
    const requestId = useMemo(() => request?.id || '', [request?.id]);
    const resourceId = useMemo(() => request?.resource_id || '', [request?.resource_id]);
    const userId = useMemo(() => request?.uid || '', [request?.uid]);
    
    // Log request data only once when component loads with a new request ID
    // Use a ref to track the last logged request ID to prevent duplicate logs on re-renders
    const lastLoggedRequestId = useRef<string>('');
    
    useEffect(() => {
        // Only log if this is a new request ID that hasn't been logged yet
        if (requestId && requestId !== lastLoggedRequestId.current) {
            lastLoggedRequestId.current = requestId;
        }
    }, [requestId]); // Only run when requestId changes, ref prevents duplicate logs even if effect runs multiple times

    const { currentMember } = useLoadMember(userId);
    const { memberships } = useLoadMemberships();
    const { membershipRequests } = useLoadRequests();

    const allMemberships = useMemo(
        () => [...(memberships || []), ...(membershipRequests || [])],
        [memberships, membershipRequests]
    );

    const showTeamMembersSection = request.team_members !== undefined;

    const [bookingTeamMembers, setBookingTeamMembers] = useState<TeamMemberSchema[]>(request.team_members ?? []);
    const [isInviteTeamMemberModalOpen, setIsInviteTeamMemberModalOpen] = useState(false);
    const [viewTeamMemberDetails, setViewTeamMemberDetails] = useState(false);
    const [selectedTeamMemberForDetails, setSelectedTeamMemberForDetails] = useState<MembershipSchema | null>(null);

    useEffect(() => {
        if (!showTeamMembersSection) return;
        setBookingTeamMembers(normalizeTeamMembers(request.team_members ?? []));
    }, [request?.id, request?.team_members, showTeamMembersSection]);

    const handleInviteTeamMember = useCallback((member: TeamMemberInviteDetails) => {
        const normalizedEmail = member.email.trim().toLowerCase();
        if (
            bookingTeamMembers.some(
                (existing) => existing.email.trim().toLowerCase() === normalizedEmail
            )
        ) {
            return;
        }
        const newMember: TeamMemberSchema = {
            first_name: member.first_name,
            last_name: member.last_name,
            email: member.email,
            id: `team-${Date.now()}`,
            username: member.username || member.uid || '',
            avatar: member.avatar || '',
            community_id: request.community_id || '',
            status: 'invited',
            role: 'viewer',
            permissions: { community: { expanded: false, permissions: {} } },
        };
        setBookingTeamMembers((prev) => [...prev, newMember]);
    }, [bookingTeamMembers, request.community_id]);

    const handleTeamMemberClick = useCallback((teamMember: TeamMemberSchema) => {
        const membership = resolveMembershipForTeamMember(teamMember, allMemberships);
        if (membership?.uid) {
            setSelectedTeamMemberForDetails(membership);
            setViewTeamMemberDetails(true);
        }
    }, [allMemberships]);

    const handleRemoveTeamMember = useCallback((memberId: string) => {
        setBookingTeamMembers((prev) => prev.filter((member) => member.id !== memberId));
    }, []);

    const { currentResource } = useLoadCurrentResource(resourceId)
    const [selectedStatus, setSelectedStatus] = useState<BookingStatusType>(request.status || 'pending');
    
    // Determine if this is a time-based resource (kitchen, training, expert)
    const isTimeBasedResource = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        return resourceType === 'kitchen' || resourceType === 'training' || resourceType === 'expert';
    }, [currentResource?.resource_type, request?.resource_type]);

    // Determine if this resource uses schedule-based selection (training, expert)
    const usesScheduleBasedSelection = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        return resourceType === 'training' || resourceType === 'expert';
    }, [currentResource?.resource_type, request?.resource_type]);

    // Extract available dates and times from schedule for training/expert resources
    const scheduleData = useMemo(() => {
        if (!usesScheduleBasedSelection || !currentResource?.resource_inventory?.schedule) {
            return { availableDates: [], dateTimeMap: new Map<string, string[]>(), dateDurationMap: new Map<string, number>() };
        }

        const schedule = currentResource.resource_inventory.schedule as TrainingInventorySchema[];
        const dateTimeMap = new Map<string, string[]>();
        const dateDurationMap = new Map<string, number>();
        const availableDates: string[] = [];

        schedule.forEach((item) => {
            if (item.date) {
                if (!dateTimeMap.has(item.date)) {
                    availableDates.push(item.date);
                    dateTimeMap.set(item.date, []);
                }
                // Store duration for this date (use first duration found, or update if different)
                // If multiple schedule items exist for same date with different durations, use the first one
                if (item.duration && !dateDurationMap.has(item.date)) {
                    dateDurationMap.set(item.date, item.duration);
                }
                if (item.times && Array.isArray(item.times)) {
                    const existingTimes = dateTimeMap.get(item.date) || [];
                    // Normalize times to HH:mm format before adding
                    const normalizedTimes = item.times.map(time => {
                        // Try to parse and normalize the time to HH:mm format
                        const trimmedTime = time.trim();
                        // If already in HH:mm format, use it directly
                        if (/^\d{2}:\d{2}$/.test(trimmedTime)) {
                            return trimmedTime;
                        }
                        // Try to parse with moment and convert to HH:mm
                        const parsed = moment(trimmedTime, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm', 'h:mm'], true);
                        if (parsed.isValid()) {
                            return parsed.format('HH:mm');
                        }
                        // If parsing fails, return trimmed original
                        return trimmedTime;
                    });
                    dateTimeMap.set(item.date, [...existingTimes, ...normalizedTimes]);
                }
            }
        });

        // Sort dates
        availableDates.sort((a, b) => moment(a).diff(moment(b)));

        // Remove duplicate times for each date (normalized to HH:mm format)
        const uniqueDateTimeMap = new Map<string, string[]>();
        dateTimeMap.forEach((times, date) => {
            const seen = new Set<string>();
            const uniqueTimes = times.filter(time => {
                // Normalize time to HH:mm format for comparison
                const normalizedTime = time.trim();
                let normalized: string;
                if (/^\d{2}:\d{2}$/.test(normalizedTime)) {
                    normalized = normalizedTime;
                } else {
                    const parsed = moment(normalizedTime, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm', 'h:mm'], true);
                    normalized = parsed.isValid() ? parsed.format('HH:mm') : normalizedTime;
                }
                
                if (seen.has(normalized)) {
                    return false;
                }
                seen.add(normalized);
                return true;
            }).map(time => {
                // Ensure all times are in HH:mm format
                const normalizedTime = time.trim();
                if (/^\d{2}:\d{2}$/.test(normalizedTime)) {
                    return normalizedTime;
                }
                const parsed = moment(normalizedTime, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm', 'h:mm'], true);
                return parsed.isValid() ? parsed.format('HH:mm') : normalizedTime;
            }).sort();
            uniqueDateTimeMap.set(date, uniqueTimes);
        });
        
        return { availableDates, dateTimeMap: uniqueDateTimeMap, dateDurationMap };
    }, [usesScheduleBasedSelection, currentResource?.resource_inventory?.schedule]);

    // Helper to check if booking is editable
    const isBookingEditable = useMemo(() => {
        const status = request.status;
        return status !== 'completed' && status !== 'canceled';
    }, [request.status]);

    // const [localKitchen, setLocalKitchen] = useState(request.resource_name || '');
    const [plan, setPlan] = useState(request.plan_name ? `${request.plan_name} : $${request.plan_rate}/hr` : '');
    // const [startDate, setStartDate] = useState(request.start_time ? moment(request.start_time).format('MM/DD/YYYY') : '');
    // const [endDate, setEndDate] = useState(request.end_time ? moment(request.end_time).format('MM/DD/YYYY') : '');
    // Initialize availableTime based on resource type
    const initialAvailableTime = useMemo(() => {
        if (!request.start_time) return '';
        
        const resourceType = currentResource?.resource_type || request?.resource_type;
        if (resourceType === 'training' || resourceType === 'expert') {
            // For training/expert, store just the start time in HH:mm format
            return moment(request.start_time).tz(request.timezone || 'America/New_York').format('HH:mm');
        } else {
            // For kitchen, store the time range
            return request.start_time && request.end_time 
                ? `${moment(request.start_time).tz(request.timezone || 'America/New_York').format('h:mmA')} - ${moment(request.end_time).tz(request.timezone || 'America/New_York').format('h:mmA')}` 
                : '';
        }
    }, [request.start_time, request.end_time, request.timezone, currentResource?.resource_type, request?.resource_type]);
    
    const [availableTime, setAvailableTime] = useState(initialAvailableTime);
    
    // Initialize totalHours - for schedule-based resources, calculate from schedule duration based on date
    const initialTotalHours = useMemo(() => {
        if (usesScheduleBasedSelection && request.start_time && currentResource?.resource_inventory?.schedule) {
            const requestDateStr = moment(request.start_time).format('YYYY-MM-DD');
            const schedule = currentResource.resource_inventory.schedule as TrainingInventorySchema[];
            // Find the first schedule item for this date to get duration
            const scheduleItem = schedule.find(item => item.date === requestDateStr);
            if (scheduleItem?.duration) {
                return scheduleItem.duration;
            }
        }
        return request.total_hours || 0;
    }, [usesScheduleBasedSelection, request.start_time, request.total_hours, currentResource?.resource_inventory?.schedule]);
    
    const [totalHours, setTotalHours] = useState(initialTotalHours);
    const [additionalFees, setAdditionalFees] = useState<Partial<FeeSchema[]>>(request.fees || []);
    const [additionalDiscounts, setAdditionalDiscounts] = useState<DiscountSchema[]>(request.discounts || []);
    const [orderInventoryItems, setOrderInventoryItems] = useState<OrderItemSchema[]>(
        request?.order_items ?? request?.order_inventory ?? []
    );
    const [additionalNotes, setAdditionalNotes] = useState(request.notes || '');


    const checkDiscountActive = useCallback((discount: DiscountSchema): boolean => {
        if (!discount?.rate) return false;
        
        // For time-based resources, check if booking dates fall within discount period
        if (isTimeBasedResource) {
            if (!request.start_time || !request.end_time) return false;
            
            const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
            const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
            const bookingStart = moment(request.start_time).startOf('day');
            const bookingEnd = moment(request.end_time).endOf('day');
            
            // Check if the booking period falls entirely within the discount period
            const isValid = bookingStart.isSameOrAfter(discountStartDate) && 
                            bookingStart.isSameOrBefore(discountEndDate) &&
                            bookingEnd.isSameOrAfter(discountStartDate) &&
                            bookingEnd.isSameOrBefore(discountEndDate);
            
            return isValid;
        }
        
        // For non-time-based resources, check if discount has valid dates
        // If no dates specified, assume it's always active
        if (!discount.start_date && !discount.end_date) return true;
        
        const now = moment();
        const discountStartDate = discount.start_date ? moment(discount.start_date).startOf('day') : moment().startOf('day');
        const discountEndDate = discount.end_date ? moment(discount.end_date).endOf('day') : moment().endOf('day');
        
        return now.isSameOrAfter(discountStartDate) && now.isSameOrBefore(discountEndDate);
      }, [request.start_time, request.end_time, isTimeBasedResource]);
    
    const [showCalendar, setShowCalendar] = useState(false);
    // Initialize selectedDay - for schedule-based resources, prefer request.start_time, fallback to schedule
    const initialSelectedDay = useMemo(() => {
        if (request?.start_time) {
            const requestDate = new Date(request.start_time);
            // For schedule-based resources, always use request.start_time as default
            if (usesScheduleBasedSelection) {
                return requestDate;
            } else {
                return requestDate;
            }
        }
        // For schedule-based, try to use first available date
        if (usesScheduleBasedSelection && currentResource?.resource_inventory?.schedule) {
            const schedule = currentResource.resource_inventory.schedule as TrainingInventorySchema[];
            if (schedule.length > 0 && schedule[0].date) {
                return new Date(schedule[0].date);
            }
        }
        return new Date();
    }, [request?.start_time, usesScheduleBasedSelection, currentResource?.resource_inventory?.schedule]);
    
    const [selectedDay, setSelectedDay] = useState<Date>(initialSelectedDay);
    
    // Track if user has explicitly changed the date/time/status
    const [dateChanged, setDateChanged] = useState(false);
    const [timeChanged, setTimeChanged] = useState(false);
    const [statusChanged, setStatusChanged] = useState(false);
    
    // Update selectedDay when initialSelectedDay changes (e.g., when schedule loads)
    // But only if user hasn't explicitly changed it
    useEffect(() => {
        if (!dateChanged && usesScheduleBasedSelection && request?.start_time) {
            const requestDate = new Date(request.start_time);
            setSelectedDay(requestDate);
        } else if (usesScheduleBasedSelection && scheduleData.availableDates.length > 0) {
            const currentDateStr = moment(selectedDay).format('YYYY-MM-DD');
            const dateExists = scheduleData.availableDates.includes(currentDateStr);
            // Only auto-update if date doesn't exist and user hasn't changed it
            if (!dateExists && !dateChanged && scheduleData.availableDates[0]) {
                // Try to use request.start_time date first, even if not in schedule
                if (request?.start_time) {
                    const requestDate = new Date(request.start_time);
                    setSelectedDay(requestDate);
                } else {
                    // Fallback to first available date
                    const [year, month, day] = scheduleData.availableDates[0].split('-').map(Number);
                    setSelectedDay(new Date(year, month - 1, day));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleData.availableDates, usesScheduleBasedSelection, request?.start_time, dateChanged]);
        // Note: selectedDay is intentionally excluded from dependencies to prevent infinite loops
        // The effect only needs to run when schedule data or request changes, not when selectedDay changes
    
    // Ensure availableTime defaults to request.start_time if empty and user hasn't changed it
    useEffect(() => {
        if (usesScheduleBasedSelection && !timeChanged && !availableTime && request?.start_time) {
            const requestTimeStr = moment(request.start_time).format('HH:mm');
            setAvailableTime(requestTimeStr);
            // Duration will be set from schedule based on date, not from request
        }
    }, [usesScheduleBasedSelection, timeChanged, availableTime, request?.start_time]);
    
    // Update duration from schedule when date is selected and schedule data is available
    useEffect(() => {
        if (usesScheduleBasedSelection && selectedDay && scheduleData.dateDurationMap.size > 0) {
            const selectedDateStr = moment(selectedDay).format('YYYY-MM-DD');
            const durationForDate = scheduleData.dateDurationMap.get(selectedDateStr);
            if (durationForDate) {
                setTotalHours(durationForDate);
            }
        }
    }, [usesScheduleBasedSelection, selectedDay, scheduleData.dateDurationMap]);
    
    // Sync all state with latest request prop values when component loads or request ID changes
    // This ensures we always have the latest request data when viewing a different request
    useEffect(() => {
        if (request) {
            // Update status from latest request
            setSelectedStatus(request.status || 'pending');
            
            // Update plan from latest request
            if (request.plan_name && request.plan_rate) {
                setPlan(`${request.plan_name} : $${request.plan_rate}/hr`);
            } else {
                setPlan('');
            }
            
            // Update fees from latest request
            setAdditionalFees(request.fees || []);
            
            // Update discounts from latest request
            setAdditionalDiscounts(request.discounts || []);

            // Update order items from latest request
            setOrderInventoryItems(request.order_items ?? request.order_inventory ?? []);
            
            // Update notes from latest request
            setAdditionalNotes(request.notes || '');
            
            // Update totalHours from latest request (for non-schedule-based or as fallback)
            if (!usesScheduleBasedSelection && request.total_hours !== undefined) {
                setTotalHours(request.total_hours);
            }
            
            // Update availableTime from latest request
            if (request.start_time) {
                const resourceType = currentResource?.resource_type || request?.resource_type;
                if (resourceType === 'training' || resourceType === 'expert') {
                    const timeStr = moment(request.start_time).tz(request.timezone || 'America/New_York').format('HH:mm');
                    setAvailableTime(timeStr);
                } else if (request.start_time && request.end_time) {
                    const timeRange = `${moment(request.start_time).tz(request.timezone || 'America/New_York').format('h:mmA')} - ${moment(request.end_time).tz(request.timezone || 'America/New_York').format('h:mmA')}`;
                    setAvailableTime(timeRange);
                } else {
                    setAvailableTime('');
                }
            } else {
                setAvailableTime('');
            }
            
            // Reset change tracking flags when new request is loaded
            setDateChanged(false);
            setTimeChanged(false);
            setStatusChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request?.id]); // Only sync when request ID changes (new request loaded)
    
    // Keep status in sync with latest request.status when request changes
    // This ensures the status dropdown always shows the current request status
    // Only sync if user hasn't manually changed the status
    useEffect(() => {
        if (request?.status && !statusChanged) {
            // Only update if user hasn't manually changed the status
            // This ensures request.status is always reflected when component loads or request updates
            setSelectedStatus(request.status);
        }
    }, [request?.status, request?.id, statusChanged]); // Sync when request.status or request.id changes, but respect user changes
    
    const [availableTimeBlocks, setAvailableTimeBlocks] = useState<any[]>();
    // const [selectedBlock, setSelectedBlock] = useState<any>();
    const [currentCalendarDate, setCurrentDate] = useState<Date>(new Date());

    // Memoize subtotal cost calculation
    const subtotal = useMemo(() => {
        if (isTimeBasedResource) {
            const baseCost = (request?.plan_rate && request?.plan_recurrence === 'hourly') 
                ? (request.plan_rate * totalHours) 
                : (request?.subtotal_cost ? request?.subtotal_cost :  0);
            return baseCost;
        } else {
            // For non-time-based resources, use subtotal_cost or total_cost
            // Special case: if resource_type !== "kitchen" and subtotal_cost is null/0/undefined but total_cost exists and > 0, use total_cost
            const resourceType = currentResource?.resource_type || request?.resource_type;
            if (resourceType !== 'kitchen' && (!request?.subtotal_cost || request.subtotal_cost === 0) && request?.total_cost && request.total_cost > 0) {
                return request.total_cost;
            }
            return request?.subtotal_cost || request?.total_cost || 0;
        }
    }, [request?.plan_rate, request?.plan_recurrence, totalHours, request?.subtotal_cost, request?.total_cost, isTimeBasedResource, currentResource?.resource_type, request?.resource_type]);

    const isKitchenBooking = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        return resourceType === 'kitchen';
    }, [currentResource?.resource_type, request?.resource_type]);

    const catalogInventoryItems = useMemo(() => {
        if (!currentResource?.resource_inventory) return [] as InventorySchema[];

        return Object.entries(currentResource.resource_inventory)
            .flatMap(([key, items]) => {
                if (key === 'schedule' || !Array.isArray(items)) return [];
                return items.filter((item): item is InventorySchema =>
                    Boolean(item && typeof item === 'object' && 'item_name' in item && !('date' in item))
                );
            });
    }, [currentResource?.resource_inventory]);

    /** Prefer the resource catalog plan so dual `plan_type` / associated inventory are available. */
    const pricingPlan = useMemo((): PlanSchema | ResourcePlanSchema | undefined => {
        const availablePlans = currentResource?.plans || [];
        if (!request?.plan_id) {
            return undefined;
        }
        const matched = availablePlans.find((plan) => plan.plan_id === request.plan_id);
        if (!matched) {
            return {
                plan_id: request.plan_id,
                plan_name: request.plan_name,
                plan_rate: request.plan_rate,
                plan_recurrence: request.plan_recurrence,
                plan_description: request.plan_description,
            };
        }
        return {
            ...matched,
            plan_id: request.plan_id || matched.plan_id,
            plan_name: request.plan_name || matched.plan_name,
            plan_rate: request.plan_rate ?? matched.plan_rate,
            plan_recurrence: request.plan_recurrence || matched.plan_recurrence,
            plan_description: request.plan_description || matched.plan_description,
        };
    }, [
        currentResource?.plans,
        request?.plan_id,
        request?.plan_name,
        request?.plan_rate,
        request?.plan_recurrence,
        request?.plan_description,
    ]);

    const applyInventoryLimits = shouldApplyMemberInventoryLimits(pricingPlan, currentMember);
    const inventoryLimitsForPricing = useMemo(
        () => getInventoryLimitsForBookingPricing(currentMember, pricingPlan, request),
        [currentMember, pricingPlan, request]
    );

    const inventoryCostBreakdown = useMemo(
        () =>
            calculateInventoryCostLinesWithLimits(
                orderInventoryItems.map((item) => ({
                    inventoryId: item.inventory_id || item.id || '',
                    totalQuantity: item.total_quantity,
                    unitPrice: item.unit_price,
                })),
                inventoryLimitsForPricing,
                applyInventoryLimits
            ),
        [applyInventoryLimits, inventoryLimitsForPricing, orderInventoryItems]
    );

    const inventoryCost = inventoryCostBreakdown.inventoryCost;

    const totalFromFees = useMemo(() => {
        return additionalFees.reduce((total, fee) => total + (fee?.rate || 0), 0);
    }, [additionalFees]);

    const totalFromDiscounts = useMemo(() => {
        const amount = (additionalDiscounts && additionalDiscounts.length > 0) 
        ? additionalDiscounts?.reduce((acc: number, discount: DiscountSchema) => {
            if (checkDiscountActive(discount)) {
                if (discount.discount_type === 'fixed') {
                return acc + discount.rate;
                } else if (discount.discount_type === 'percentage') {
                return acc + (discount.rate * (subtotal || 0) / 100);
                }
            }
            return acc;
            }, 0)
        : 0;
            
            return amount;
        }, 
    [additionalDiscounts, checkDiscountActive, subtotal]);

    // var total = subtotal + totalFromFees - totalFromDiscounts;
    const total = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        // For funding resources, don't apply fees and discounts - use total_cost if available, otherwise subtotal_cost
        if (resourceType === 'funding') {
            if (request?.total_cost && request.total_cost > 0) {
                return request.total_cost;
            }
            return request?.subtotal_cost || 0;
        }
        
        // If status is "completed", don't apply fees and discounts
        if (request?.status === 'completed') {
            if (resourceType !== 'kitchen' && (!request?.subtotal_cost || request.subtotal_cost === 0) && request?.total_cost && request.total_cost > 0) {
                return request.total_cost;
            }
            return Math.max(0, (subtotal || 0) + inventoryCost);
        }
        
        // Special case: if resource_type !== "kitchen" and subtotal_cost is null/0/undefined but total_cost exists and > 0,
        // use request.total_cost as base, then add fees and subtract discounts
        if (resourceType !== 'kitchen' && (!request?.subtotal_cost || request.subtotal_cost === 0) && request?.total_cost && request.total_cost > 0) {
            const baseTotal = request.total_cost + totalFromFees + inventoryCost;
            const finalTotal = baseTotal - totalFromDiscounts;
            return Math.max(0, finalTotal); // Ensure total is never negative
        }
        const baseTotal = (subtotal || 0) + totalFromFees + inventoryCost;
        const finalTotal = baseTotal - totalFromDiscounts;
        return Math.max(0, finalTotal); // Ensure total is never negative
      }, [subtotal, totalFromDiscounts, totalFromFees, inventoryCost, currentResource?.resource_type, request?.resource_type, request?.subtotal_cost, request?.total_cost, request?.status]);

    // Helper to check if fees and discounts should be shown
    const shouldShowFeesAndDiscounts = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        return resourceType !== 'funding' && total > 0;
    }, [currentResource?.resource_type, request?.resource_type, total]);

    const shouldShowCostBreakdown = useMemo(() => {
        const resourceType = currentResource?.resource_type || request?.resource_type;
        if (resourceType === 'funding') return false;
        return subtotal > 0 || totalFromFees > 0 || totalFromDiscounts > 0 || orderInventoryItems.length > 0;
    }, [currentResource?.resource_type, request?.resource_type, subtotal, totalFromFees, totalFromDiscounts, orderInventoryItems.length]);

    const shouldShowOrderItems = useMemo(() => {
        return isKitchenBooking && (orderInventoryItems.length > 0 || isBookingEditable);
    }, [isKitchenBooking, orderInventoryItems.length, isBookingEditable]);


    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    // const statusOptionsForDropdown = [
    //     { value: 'in_progress', label: 'In Progress' },
    //     { value: 'inactive', label: 'Inactive' }
    // ].map(option => {
    //     const colors = getBookingStatusColor(option.value);
    //     return {
    //         ...option,
    //         color: colors.primaryTextColor,
    //         bgColor: colors.bgColor
    //     }
    // });

    // Memoize status options to ensure they update when request.status or resource_type changes
    const statusOptionsForDropdown = useMemo(() => {
        return getBookingStatusOptions(request?.resource_type as ResourceType, request?.status || 'pending').map((status: BookingStatusType) => ({
            value: status as BookingStatusType,
            label: getBookingStatusLabel(status as BookingStatusType),
            color: getBookingStatusColor(status as BookingStatusType).primaryTextColor,
            bgColor: getBookingStatusColor(status as BookingStatusType).bgColor
        }));
    }, [request?.resource_type, request?.status]);

    // Use selectedStatus for display (what will be saved), but ensure it's synced with request.status
    // The dropdown highlighting uses request.status to show what's currently saved
    const selectedStatusLabel = getBookingStatusLabel(selectedStatus);
    const selectedStatusColors = getBookingStatusColor(selectedStatus);



    const handleStatusUpdate = (status: BookingStatusType) => {
        setSelectedStatus(status);
        setStatusChanged(true);
        setStatusDropdownOpen(false);
    }

    const { updateBooking } = useUpdateBooking(request?.id ? request.id : "", request?.resource_id, request?.resource_type)

    const handleUpdateBooking = async () => {
        
        const bookingId = request?.id || ""
        const communityId = request?.community_id || ""
    
        if (bookingId && communityId && request) {
          const updatedForm = {
            ...request,
            status: selectedStatus,
            total_cost: total,
            notes: additionalNotes
          };

          // Only include time-based fields for time-based resources
          if (isTimeBasedResource) {
            updatedForm.total_hours = totalHours;
            updatedForm.plan_id = request?.plan_id;
            updatedForm.plan_name = request?.plan_name;
            updatedForm.plan_recurrence = request?.plan_recurrence;
            updatedForm.plan_rate = request?.plan_rate;
          }


          // Only update time fields for time-based resources
          if (isTimeBasedResource) {
            if (usesScheduleBasedSelection && selectedDay && availableTime) {
              // For training/expert, availableTime is just the start time in HH:mm format
              const selectedDateStr = moment(selectedDay).format('YYYY-MM-DD');
              
              // Get duration from schedule data or use totalHours as fallback
              const durationForDate = scheduleData.dateDurationMap.get(selectedDateStr);
              const duration = durationForDate || totalHours || 1;
              
              // Create start and end moments from selected date and time
              const startMoment = moment(`${selectedDateStr} ${availableTime}`, 'YYYY-MM-DD HH:mm');
              const endMoment = startMoment.clone().add(duration, 'hours');
              
              // Apply timezone and format
              updatedForm.start_time = startMoment.tz(request?.timezone || 'America/New_York').format();
              updatedForm.end_time = endMoment.tz(request?.timezone || 'America/New_York').format();
            } else {
              // For kitchen resources, parse the time range format
              const availableTimeSplit = availableTime.split('-')
              //   console.log("availableTimeSplit", availableTimeSplit)
              if (availableTime && availableTimeSplit.length > 1) {
                // Parse the time strings and create proper moment objects with the selected date
                const startTimeStr = availableTimeSplit[0].trim();
                const endTimeStr = availableTimeSplit[1].trim();
                
                // Create moment objects by combining the selected date with the time strings
                const startMoment = moment(`${moment(selectedDay).format('YYYY-MM-DD')} ${startTimeStr}`, 'YYYY-MM-DD h:mmA');
                const endMoment = moment(`${moment(selectedDay).format('YYYY-MM-DD')} ${endTimeStr}`, 'YYYY-MM-DD h:mmA');
                
                // Apply timezone and format
                updatedForm.start_time = startMoment.tz(request?.timezone || 'America/New_York').format();
                updatedForm.end_time = endMoment.tz(request?.timezone || 'America/New_York').format();
              }
            }
          }
    
        //   if (additionalFees && additionalFees.length > 0) {
        //     updatedForm.fees = additionalFees as FeeSchema[]
        //   }

        if (additionalFees !== request?.fees) {
            updatedForm.fees = additionalFees as FeeSchema[];
        }

        if (additionalDiscounts !== request?.discounts) {
            updatedForm.discounts = additionalDiscounts;
        }

        const originalOrderItems = request?.order_items ?? request?.order_inventory ?? [];
        if (JSON.stringify(orderInventoryItems) !== JSON.stringify(originalOrderItems)) {
            updatedForm.order_items = orderInventoryItems;
        }

        if (showTeamMembersSection && JSON.stringify(bookingTeamMembers) !== JSON.stringify(request?.team_members ?? [])) {
            updatedForm.team_members = bookingTeamMembers;
        }
          
    
        //   if (paymentMethod?.id) {
        //     updatedForm.payment_method = paymentMethod?.id
        //   }
    
          const changedAttributes = getChangedAttributes(request || {} as ResourceBookingSchema, updatedForm);
          // Make start_time and end_time timezone aware if they exist in changedAttributes
          if (changedAttributes.start_time || changedAttributes.end_time) {
            const timezone = request?.timezone || 'America/New_York';
            
            if (changedAttributes.start_time) {
              changedAttributes.start_time = moment(changedAttributes.start_time).tz(timezone).format();
            }
            
            if (changedAttributes.end_time) {
              changedAttributes.end_time = moment(changedAttributes.end_time).tz(timezone).format();
            }
          }

        //   console.log("updatedForm", updatedForm)
        //   console.log("changedAttributes", changedAttributes)
          
          if (bookingId && communityId && Object.keys(changedAttributes).length > 0 ){
            await updateBooking({ status: selectedStatus, ...changedAttributes });
          }
        }
        handleClose();
    }

    // const kitchenOptions = ['DOL Kitchen', 'JFK Kitchen', 'LAX Kitchen'];
    // const planOptions = ['Flexi : $40/hr', 'Monthly : $35/hr', 'Annual : $30/hr'];
    // const timeOptions = ['8:00AM - 9:00AM', '9:00AM - 10:00AM', '10:00AM - 11:00AM'];

    const [isOpenFee, setIsOpenFee] = useState(false);
    const [isOpenDiscount, setIsOpenDiscount] = useState(false);
    
    const handleAddFee = async (fee: FeeSchema) => {
        addFee(fee)
        setIsOpenFee(false);
    }

    const handleAddDiscount = async (discount: DiscountSchema) => {
        setAdditionalDiscounts(discounts => [...discounts, discount] as DiscountSchema[]);
        setIsOpenDiscount(false);
    }
    
    const addFee = (fee: FeeSchema) => {
        setAdditionalFees(fees => [...fees, fee]);
    };

    const removeFee = (index: number) => {
        setAdditionalFees(fees => fees.filter((_, i) => i !== index));
    };

    const removeDiscount = (index: number) => {
        setAdditionalDiscounts(discounts => discounts.filter((_, i) => i !== index));
    }

    const addOrderItem = (inventoryId: string) => {
        const catalogItem = catalogInventoryItems.find(item => item.id === inventoryId);
        if (!catalogItem?.id) return;

        const catalogItemId = catalogItem.id;

        setOrderInventoryItems(items => {
            if (items.some(item => item.inventory_id === catalogItemId)) {
                return items;
            }

            return [...items, {
                id: `order-item-${catalogItemId}-${Date.now()}`,
                inventory_id: catalogItemId,
                item_name: catalogItem.item_name,
                category: catalogItem.category,
                measure_unit: catalogItem.measure_unit,
                unit_price: catalogItem.unit_price ?? 0,
                total_quantity: 1,
                resource_type: 'kitchen',
            } as OrderItemSchema];
        });
    };

    const updateOrderItemQuantity = (index: number, quantity: number) => {
        const normalizedQuantity = Math.max(1, Math.floor(quantity) || 1);
        setOrderInventoryItems(items => items.map((item, i) =>
            i === index ? { ...item, total_quantity: normalizedQuantity } : item
        ));
    };

    const removeOrderItem = (index: number) => {
        setOrderInventoryItems(items => items.filter((_, i) => i !== index));
    };;

    const nextMonth = () => {
        setCurrentDate(addMonths(currentCalendarDate, 1));
      };
    
      const prevMonth = () => {
        const newDate = addMonths(currentCalendarDate, -1);
        // Only allow going back if the new date is not before today
        // if (newDate >= new Date()) {
          setCurrentDate(newDate);
        // }
    };


    const handleDateChange = useCallback((date: Date) => {
        setSelectedDay(date);
        setDateChanged(true);
        
        // For schedule-based resources (training/expert), automatically select first available time when date changes
        if (usesScheduleBasedSelection) {
            const dateStr = moment(date).format('YYYY-MM-DD');
            const timesForNewDate = scheduleData.dateTimeMap.get(dateStr) || [];
            
            // Set duration from schedule for the selected date
            const durationForDate = scheduleData.dateDurationMap.get(dateStr);
            if (durationForDate) {
                setTotalHours(durationForDate);
            }
            
            // Check if request.start_time time exists for this date
            let timeToUse = '';
            if (!timeChanged && request?.start_time) {
                const requestTimeStr = moment(request.start_time).format('HH:mm');
                const requestDateStr = moment(request.start_time).format('YYYY-MM-DD');
                // If the date matches and time exists in schedule for this date, use it
                if (requestDateStr === dateStr && timesForNewDate.includes(requestTimeStr)) {
                    timeToUse = requestTimeStr;
                }
            }
            
            if (!timeToUse && timesForNewDate.length > 0) {
                // Automatically select the first available time for the new date
                timeToUse = timesForNewDate[0];
            }
            
            if (timeToUse) {
                setAvailableTime(timeToUse);
            } else {
                // No times available for this date, use request.start_time if available
                if (!timeChanged && request?.start_time) {
                    const requestTimeStr = moment(request.start_time).format('HH:mm');
                    const requestDateStr = moment(request.start_time).format('YYYY-MM-DD');
                    if (requestDateStr === dateStr) {
                        setAvailableTime(requestTimeStr);
                        // Duration already set from schedule above
                    } else {
                        setAvailableTime('');
                        // If date doesn't match, reset duration only if no schedule duration found
                        if (!durationForDate) {
                            setTotalHours(0);
                        }
                    }
                } else {
                    setAvailableTime('');
                    // Reset duration only if no schedule duration found
                    if (!durationForDate) {
                        setTotalHours(0);
                    }
                }
            }
            return;
        }
        
        if (request?.start_time && request?.end_time) {
          let newStartTime: moment.Moment, newEndTime: moment.Moment;
          
          // If we have available time blocks, use the first one's time portions
          if (availableTimeBlocks && availableTimeBlocks.length > 0) {
            const firstBlock = availableTimeBlocks[0];
            newStartTime = moment(date).set({
              hour: moment(firstBlock.start).hour(),
              minute: moment(firstBlock.start).minute()
            });
            newEndTime = moment(date).set({
              hour: moment(firstBlock.end).hour(),
              minute: moment(firstBlock.end).minute()
            });
          } else {
            // Fall back to current form values if no available blocks
            const currentStartTime = moment(request.start_time);
            const currentEndTime = moment(request.end_time);
            
            newStartTime = moment(date).set({
              hour: currentStartTime.hour(),
              minute: currentStartTime.minute()
            });
            newEndTime = moment(date).set({
              hour: currentEndTime.hour(),
              minute: currentEndTime.minute()
            });
          }
    
          // setStartDate(newStartTime.format('YYYY-MM-DDTHH:mm'));
          setAvailableTime(`${newStartTime.format('h:mmA')} - ${newEndTime.format('h:mmA')}`);
        }
        setShowCalendar(false);
    }, [request, availableTimeBlocks, usesScheduleBasedSelection, scheduleData.dateTimeMap, scheduleData.dateDurationMap, timeChanged]);    

    const handleTimeChange = (startTime: moment.Moment, endTime: moment.Moment) => {
        if (request) {
          const duration = moment.duration(endTime.diff(startTime));
          const hoursDiff = duration.asHours();
          setTotalHours(hoursDiff);
          
          // For schedule-based resources, store time in HH:mm format
          if (usesScheduleBasedSelection) {
            setAvailableTime(startTime.format('HH:mm'));
          } else {
            // For kitchen resources, store time range
            // Ensure the times are set on the selected day
            const newStartTime = moment(selectedDay).set({
              hour: startTime.hour(),
              minute: startTime.minute()
            });
            const newEndTime = moment(selectedDay).set({
              hour: endTime.hour(),
              minute: endTime.minute()
            });
      
            // Use the same format as the select options for consistency
            setAvailableTime(`${newStartTime.format('hh:mm A')} - ${newEndTime.format('hh:mm A')}`);
          }
        }
      };
    
      const handleTimeBlockSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedBlock = availableTimeBlocks?.find(block => 
          `${moment(block.start).format("hh:mm A")} - ${moment(block.end).format("hh:mm A")}` === e.target.value
        );
        
        if (selectedBlock) {
          handleTimeChange(moment(selectedBlock.start), moment(selectedBlock.end));
        }
      };



    const { getPaymentMethod } = useGetPaymentMethods(request?.uid ? request.uid : "")


    const handleGetPaymentMethod = () => request ? getPaymentMethod(request) : {}

    var currentPaymethod = handleGetPaymentMethod()


    const [messageOpen, setMessageOpen] = useState(false);

    const getCardIcon = (paymentMethod: string) => {
        switch (paymentMethod) {
          case 'visa':
            return <VisaCardIcon className='w-8 my-auto' />;
          case 'mastercard':
            return <MasterCardIcon className='w-8 my-auto' />;
          case 'amex':
            return <AmexCardIcon className='w-8 my-auto' />;
          // Add cases for other card types
          default:
            return <img src="/icons/unknown.svg" alt="Unknown" />;
        }
    };

    const [imageError, setImageError] = useState(false);
    const avatar = currentMember?.avatar || request?.user_avatar;

    return (
        <>
            <div className="flex flex-col h-full max-h-full bg-white rounded-lg font-raleway w-full max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-start mb-4 flex-shrink-0">
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">Manage {capitalizeWords(request?.resource_type || '')} Request</h1>
                        <p className="text-sm text-gray-500 mt-1">View and edit the member's booking details</p>
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                            className="flex items-center space-x-2 px-3 py-2 border rounded-md text-sm font-semibold"
                            style={{ color: selectedStatusColors.primaryTextColor, backgroundColor: selectedStatusColors.bgColor, borderColor: selectedStatusColors.primaryTextColor }}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedStatusColors.primaryTextColor }}></span>
                            <span>{selectedStatusLabel}</span>
                            {statusDropdownOpen ? <ExpandLess className="w-4 h-4" /> : <ExpandMore className="w-4 h-4" />}
                        </button>
                        {statusDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg z-20 border">
                                {statusOptionsForDropdown.map((option, i) => (
                                    <button
                                        key={option.value}
                                        // disabled={option.value === request.status}
                                        onClick={() => handleStatusUpdate(option.value as BookingStatusType)}
                                        style={option.value === request.status ? { backgroundColor: option.color } : {}}
                                        className={`w-full ${i === 0 ? 'rounded-t-lg' : ''} ${i === statusOptionsForDropdown.length - 1 ? 'rounded-b-lg' : ''} text-left flex items-center space-x-2 px-4 py-2 text-sm hover:bg-gray-50 ${option.value === request.status ? ` text-white` : ''}`}
                                    >
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: option.value === request.status ? '#FFFFFF' : option.color }}></span>
                                        <span>{option.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="space-y-4 text-sm flex-1 overflow-y-auto pr-1">
                    <div className='flex flex-row p-1 my-2'>
                        {avatar && !imageError ? (
                            <div
                                className="flex border-[#E9E9E9] border-[1px] rounded-full h-[56px] w-[56px] mr-2"
                            >
                                <img
                                    src={avatar}
                                    alt="User Avatar"
                                    className="h-[56px] w-[56px] m-auto rounded-full cursor-pointer"
                                    onError={() => setImageError(true)}
                                />
                            </div>
                        ) : (
                            <UserAvatar
                                firstName={currentMember?.first_name || request?.user_full_name?.split(' ')[0]}
                                lastName={currentMember?.last_name || request?.user_full_name?.split(' ')[1]}
                                username={currentMember?.username}
                                email={currentMember?.email}
                                size="lg"
                                className="mr-2"
                            />
                        )}

                        <div className='flex flex-col mx-2'>
                            <p className='text-[16px] font-raleway text-black font-medium'>
                                {/* {currentMember?.first_name || 'N/A'} {currentMember?.last_name || 'N/A'} */}
                                {
                                    currentMember?.first_name && currentMember?.last_name
                                    ? `${currentMember.first_name} ${currentMember.last_name}`
                                    : request?.user_full_name || 'N/A'
                                }
                            </p>
                            <p className='text-[14px] font-open-sans text-[#4D4D4D] font-normal'>
                                {request.username || 'N/A'}
                            </p>



                            <div className='flex flex-row'>
                                <p className='text-[#808080] text-[12px] font-raleway mr-2'>Current Status: </p>
                                <div className="flex items-center font-open-sans">
                                    <span className="flex items-center text-[12px]" style={{color: getBookingStatusColor(request.status || '').primaryTextColor}}>
                                        <FiberManualRecord style={{fontSize: 12}} />
                                        &nbsp;
                                        <span>
                                            {(request.status) ? capitalizeWords(request.status.replace(/_/g, ' ')) : 'Pending'}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <div>
                        <label className="font-medium text-gray-700">Local Kitchen</label>
                        <select disabled value={localKitchen} onChange={e => setLocalKitchen(e.target.value)} className="mt-1 block w-full p-3 border cursor-not-allowed border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                            <option value={request.resource_name}>{request.resource_name}</option>
                        </select>
                    </div> */}

                    {/* {kitchenOptions.map(k => <option key={k} value={k}>{k}</option>)} */}

                    {/* <div>
                        <label className="font-medium text-gray-700">Member</label>
                        <div className="mt-1 p-3 border rounded-md bg-gray-50 text-gray-900">
                            {currentMember?.first_name || 'N/A'} {currentMember?.last_name || 'N/A'}
                        </div>
                    </div> */}

                    {/* Time-based resource fields (kitchen, training, expert) */}
                    {isTimeBasedResource && (
                        <>
                            {request.plan_name && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Plan</label>
                                    <select disabled value={plan} onChange={e => setPlan(e.target.value)} className="mt-1 block w-full p-3 border cursor-not-allowed border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-open-sans">
                                        <option value={request.plan_name}>{request.plan_name} - ${styleThousandCommas(request.plan_rate || 0)} / {planRecurrenceMap[request.plan_recurrence as keyof typeof planRecurrenceMap]}</option>
                                    </select>
                                </div>
                            )}

                            <div className="flex space-x-4">
                                <div className="w-full">
                                    <label className="font-medium text-gray-700">Hours</label>
                                    <input disabled={!isBookingEditable || request.status === 'in_progress' || usesScheduleBasedSelection} step={0.5} max={24} min={0.5} type="number" name="total_hours" value={totalHours.toFixed(1)} onChange={e => setTotalHours(Number(e.target.value))} className="mt-1 block w-full p-3 border font-open-sans border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="0" />
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <div className="w-full">
                                    <label className="font-medium text-gray-700 font-raleway">Date</label>
                                    {usesScheduleBasedSelection ? (
                                        // Dropdown for training/expert resources
                                        <select
                                            disabled={!isBookingEditable || request.status === 'in_progress'}
                                            value={(() => {
                                                if (!selectedDay) {
                                                    // If no selectedDay, use request.start_time if available
                                                    if (request?.start_time) {
                                                        return moment(request.start_time).format('YYYY-MM-DD');
                                                    }
                                                    return '';
                                                }
                                                const currentDateStr = moment(selectedDay).format('YYYY-MM-DD');
                                                // Ensure the value matches one of the available dates
                                                if (scheduleData.availableDates.includes(currentDateStr)) {
                                                    return currentDateStr;
                                                }
                                                // If current date is not in schedule, use request.start_time date if available
                                                if (request?.start_time) {
                                                    const requestDateStr = moment(request.start_time).format('YYYY-MM-DD');
                                                    if (currentDateStr === requestDateStr) {
                                                        return requestDateStr;
                                                    }
                                                }
                                                // Fallback to first available date
                                                return scheduleData.availableDates[0] || '';
                                            })()}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue) {
                                                    // Create date in local timezone to avoid timezone issues
                                                    const [year, month, day] = selectedValue.split('-').map(Number);
                                                    const newDate = new Date(year, month - 1, day);
                                                    setSelectedDay(newDate);
                                                    handleDateChange(newDate);
                                                } else {
                                                    // If cleared, use request.start_time if available
                                                    if (request?.start_time) {
                                                        const requestDate = new Date(request.start_time);
                                                        setSelectedDay(requestDate);
                                                        handleDateChange(requestDate);
                                                    } else if (scheduleData.availableDates.length > 0) {
                                                        // Reset to first available date if clearing
                                                        const [year, month, day] = scheduleData.availableDates[0].split('-').map(Number);
                                                        const firstDate = new Date(year, month - 1, day);
                                                        setSelectedDay(firstDate);
                                                        handleDateChange(firstDate);
                                                    } else {
                                                        setSelectedDay(new Date());
                                                        setAvailableTime('');
                                                    }
                                                }
                                            }}
                                            className="mt-1 block w-full p-3 border font-open-sans border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">-- Select Date --</option>
                                            {scheduleData.availableDates.map((date) => (
                                                <option key={date} value={date}>
                                                    {moment(date).format('ddd MMM DD, YYYY')}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        // Calendar button for kitchen resources
                                        <>
                                            <button
                                                disabled={!isBookingEditable || request.status === 'in_progress'}
                                                type="button"
                                                className="flex items-center w-full p-2 border-[1px] border-[#E9E9E9] rounded-lg bg-white focus:ring-1"
                                                onClick={() => setShowCalendar(prev => !prev)}
                                            >
                                                <span className="flex-1 text-left text-[16px] text-[#4D4D4D]  font-medium font-open-sans">
                                                    {selectedDay ? moment(selectedDay).format('MM/DD/YYYY') : 'Select date'}
                                                </span>
                                                <span className="ml-2 p-1 rounded-full bg-[#F4F8FB] text-primaryDark-200 font-open-sans">
                                                    <CalendarIcon />
                                                </span>
                                            </button>

                                            {showCalendar && (
                                                <div className="absolute z-50 lg:mt-1 p-4 min-w-[320px] max-w-2xl bg-white border border-[#E9E9E9] rounded-lg shadow-lg">
                                                    <div className="flex flex-row mb-4">
                                                        <h2 className="text-[16px]  font-raleway font-medium text-center my-auto text-primary-400">
                                                        {format(currentCalendarDate, "MMMM yyyy")}
                                                        </h2>

                                                        <div className="flex-row justify-between items-center text-slate-400 my-auto ml-auto">
                                                            <button 
                                                                onClick={prevMonth}
                                                                className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
                                                            >
                                                                <LeftArrow className=" text-black rounded-full  m-auto" />
                                                            </button>
                                                            <button 
                                                                onClick={nextMonth}
                                                                className="mr-4 rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer m-auto"
                                                            >
                                                                <RightArrow className="text-black rounded-full m-auto" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <ReservationCalendar
                                                        currentCalendarDate={currentCalendarDate}
                                                        currentResource={currentResource ? currentResource : {} as ResourceSchema}
                                                        resourceBookings={allResourceRequests}
                                                        setBlocks={setAvailableTimeBlocks}
                                                        nHours={totalHours}
                                                        selectedDay={selectedDay}
                                                        handleDateChange={handleDateChange}
                                                        
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="font-medium text-gray-700 font-raleway">
                                    Time &nbsp;<span className='text-sm my-auto font-semibold'>({moment(selectedDay).format("ddd, LL")})</span>
                                </label>
                            
                                <div className="w-full">
                                    <div className="w-full">
                                        {usesScheduleBasedSelection ? (
                                            // Time selection from schedule for training/expert
                                            <select
                                                disabled={!isBookingEditable || request.status === 'in_progress' || !selectedDay}
                                                className="w-full border-[1px]  text-[16px] text-[#4D4D4D]  border-[#E9E9E9] bg-white rounded p-3 font-open-sans"
                                                onChange={(e) => {
                                                    const selectedTime = e.target.value;
                                                    setTimeChanged(true);
                                                    if (selectedTime) {
                                                        // Update availableTime to the selected time in HH:mm format
                                                        setAvailableTime(selectedTime);
                                                        // Duration is set from date selection only, don't change it here
                                                    } else {
                                                        // If cleared, use request.start_time if available
                                                        if (request?.start_time) {
                                                            const requestTimeStr = moment(request.start_time).format('HH:mm');
                                                            setAvailableTime(requestTimeStr);
                                                            // Duration should remain from date selection, don't override
                                                        } else {
                                                            setAvailableTime('');
                                                            // Only reset duration if no date is selected
                                                            const selectedDateStr = moment(selectedDay).format('YYYY-MM-DD');
                                                            const durationForDate = scheduleData.dateDurationMap.get(selectedDateStr);
                                                            if (!durationForDate) {
                                                                setTotalHours(0);
                                                            }
                                                        }
                                                    }
                                                }}
                                                value={(() => {
                                                    // If time hasn't been changed and availableTime is empty, use request.start_time
                                                    if (!timeChanged && !availableTime && request?.start_time) {
                                                        return moment(request.start_time).format('HH:mm');
                                                    }
                                                    return availableTime || '';
                                                })()}
                                            >
                                                <option disabled value="">-- Select Time --</option>
                                                {selectedDay && (() => {
                                                    const selectedDateStr = moment(selectedDay).format('YYYY-MM-DD');
                                                    const scheduleTimes = scheduleData.dateTimeMap.get(selectedDateStr) || [];
                                                    const requestTimeStr = request?.start_time ? moment(request.start_time).format('HH:mm') : '';
                                                    const requestDateStr = request?.start_time ? moment(request.start_time).format('YYYY-MM-DD') : '';
                                                    
                                                    // Normalize all times to HH:mm format and use Set for deduplication
                                                    const allTimesSet = new Set<string>();
                                                    
                                                    // Add schedule times (already normalized in scheduleData)
                                                    scheduleTimes.forEach(time => {
                                                        const normalized = time.trim();
                                                        // Ensure it's in HH:mm format
                                                        if (/^\d{2}:\d{2}$/.test(normalized)) {
                                                            allTimesSet.add(normalized);
                                                        } else {
                                                            const parsed = moment(normalized, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm', 'h:mm'], true);
                                                            if (parsed.isValid()) {
                                                                allTimesSet.add(parsed.format('HH:mm'));
                                                            }
                                                        }
                                                    });
                                                    
                                                    // Include request time in options if it's for the selected date, even if not in schedule
                                                    if (requestDateStr === selectedDateStr && requestTimeStr) {
                                                        // Normalize request time to HH:mm format
                                                        const normalizedRequestTime = requestTimeStr.trim();
                                                        if (/^\d{2}:\d{2}$/.test(normalizedRequestTime)) {
                                                            allTimesSet.add(normalizedRequestTime);
                                                        } else {
                                                            const parsed = moment(normalizedRequestTime, ['HH:mm', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'H:mm', 'h:mm'], true);
                                                            if (parsed.isValid()) {
                                                                allTimesSet.add(parsed.format('HH:mm'));
                                                            }
                                                        }
                                                    }
                                                    
                                                    // Convert Set to sorted array
                                                    const allTimes: string[] = Array.from(allTimesSet).sort();
                                                    
                                                    return allTimes.map((time, index) => {
                                                        const timeFormatted = moment(time, 'HH:mm').format('hh:mm A');
                                                        return (
                                                            <option key={`${time}-${index}`} value={time}>
                                                                {timeFormatted}
                                                            </option>
                                                        );
                                                    });
                                                })()}
                                            </select>
                                        ) : (
                                            // Time block selection for kitchen resources
                                            <select
                                                disabled={!isBookingEditable || request.status === 'in_progress'}
                                                className="w-full border-[1px]  text-[16px] text-[#4D4D4D]  border-[#E9E9E9] bg-white rounded p-3 font-open-sans"
                                                onChange={handleTimeBlockSelect}
                                                value={availableTime}
                                            >
                                                <option disabled value="">-- Select Time Block --</option>
                                                {availableTimeBlocks?.map((block, index) => (
                                                    <option key={index} value={`${moment(block.start).format("hh:mm A")} - ${moment(block.end).format("hh:mm A")}`}>
                                                        {moment(block.start).format("hh:mm A")} - {moment(block.end).format("hh:mm A")}
                                                    </option>
                                                ))}
                                                {
                                                    !availableTimeBlocks?.length && availableTime && (
                                                        <option value={availableTime}>{availableTime}</option>
                                                    )
                                                }
                                            </select>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Resource Type - shown for all resource types */}
                    {(currentResource?.resource_type || request?.resource_type) && (
                        <div>
                            <label className="font-medium font-raleway text-gray-700">{capitalizeWords(currentResource?.resource_type || request?.resource_type || '').replace(/_/g, ' ')}</label>
                            <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                {capitalizeWords((currentResource?.name || request?.resource_name || '').replace(/_/g, ' '))}
                            </div>
                        </div>
                    )}

                    {/* Request Date - shown for kitchen, training, farm, and expert resource types */}
                    {(() => {
                        const resourceType = currentResource?.resource_type || request?.resource_type;
                        const shouldShowRequestDate = resourceType === 'kitchen' || resourceType === 'training' || resourceType === 'farm' || resourceType === 'expert';
                        return shouldShowRequestDate && request?.created ? (
                            <div>
                                <label className="font-medium font-raleway text-gray-700">Request Date</label>
                                <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                    {moment(request.created).format('MM/DD/YYYY')}
                                </div>
                            </div>
                        ) : null;
                    })()}

                    {/* Business-based resource fields (documentation, funding) */}
                    {!isTimeBasedResource && (
                        <>
                            {request.business_name && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Business Name</label>
                                    <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                        {request.business_name}
                                    </div>
                                </div>
                            )}

                            {request.location && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Location</label>
                                    <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                        {request.location}
                                    </div>
                                </div>
                            )}

                            {request.business_type && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Business Type</label>
                                    <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                        {request.business_type}
                                    </div>
                                </div>
                            )}

                            {request.business_category && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Business Category</label>
                                    <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                        {request.business_category}
                                    </div>
                                </div>
                            )}

                            {request.start_time && (
                                <div>
                                    <label className="font-medium font-raleway text-gray-700">Request Date</label>
                                    <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-900 font-open-sans">
                                        {moment(request.start_time).format('MM/DD/YYYY')}
                                    </div>
                                </div>
                            )}
                        </>
                    )}               

                    {showTeamMembersSection && (
                        <div>
                            <label className="block text-[#808080] text-[16px] font-raleway font-medium">Team Members</label>

                            {bookingTeamMembers.length > 0 && (
                                <div className="w-full space-y-2 mt-1 py-2">
                                    {bookingTeamMembers.map((member, index) => (
                                        <div
                                            key={member.id || member.email || `team-member-${index}`}
                                            className="flex items-center gap-2"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleTeamMemberClick(member)}
                                                className="flex items-center gap-2 cursor-pointer text-[14px] text-medium font-open-sans text-[#333333] hover:text-primary-400 text-left"
                                            >
                                                <UserAvatar
                                                    firstName={member.first_name}
                                                    lastName={member.last_name}
                                                    email={member.email}
                                                    avatar={member.avatar}
                                                    size="sm"
                                                />
                                                {getTeamMemberDisplayName(member)}
                                            </button>
                                            {/* {member.email && (
                                                <span className="text-[13px] text-[#808080] font-open-sans truncate">
                                                    {member.email}
                                                </span>
                                            )} */}
                                            {isBookingEditable && member.id && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTeamMember(member.id!)}
                                                    className="ml-auto p-1 text-gray-500 hover:text-red-600"
                                                    aria-label={`Remove ${getTeamMemberDisplayName(member)}`}
                                                >
                                                    <Close sx={{ fontSize: 18 }} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!isBookingEditable && bookingTeamMembers.length === 0 && (
                                <div className="mt-1 block w-full p-3 border border-[#E9E9E9] rounded-md bg-gray-50 text-gray-500 font-open-sans text-[14px]">
                                    No team members on this booking
                                </div>
                            )}

                            {isBookingEditable && (
                                <button
                                    type="button"
                                    onClick={() => setIsInviteTeamMemberModalOpen(true)}
                                    className="flex items-center space-x-2 text-primary-300 font-semibold py-2 px-1 mt-1"
                                >
                                    <span className="text-lg font-bold">+</span>
                                    <span>Add team member</span>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Additional Fees - hidden for funding resource type or when total is 0 */}
                    {shouldShowFeesAndDiscounts && (additionalFees.length > 0 || isBookingEditable) && (
                        <div>
                            <label className="font-medium text-gray-700">Additional Fees</label>
                            <div className="mt-1 p-3 border-[2px] border-dashed border-[#E9E9E9] rounded-md space-y-3 bg-gray-50/50">
                                {additionalFees.map((fee, index) => {
                                    if (!fee || typeof fee.rate !== 'number' || !fee.name) return null;
                                    return (
                                        <div key={index}>
                                            <p className="text-gray-700 font-medium mb-1 text-sm font-raleway">{fee.name}</p>
                                            <div className="flex justify-between items-center bg-white p-3 rounded-md border-[1px] border-[#E9E9E9] font-open-sans">
                                                <p className="text-gray-800 font-semibold">${fee.rate.toFixed(2)}</p>
                                                <div className="flex items-center space-x-2">
                                                    <button disabled={!isBookingEditable} onClick={() => removeFee(index)} className="p-1 text-gray-500 hover:text-red-600"><Close sx={{ fontSize: 20 }} /></button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {isBookingEditable && (
                                    <button disabled={!isBookingEditable} onClick={() => setIsOpenFee(true)} className="flex items-center space-x-2 text-primary-300 font-semibold py-2 px-1">
                                        <span className="text-lg font-bold">+</span>
                                        <span>Add a Custom Fee</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Order Items - kitchen bookings only */}
                    {shouldShowOrderItems && (
                        <div>
                            <label className="font-medium text-gray-700">Order Items</label>
                            <div className="mt-1 p-3 border-[2px] border-dashed border-[#E9E9E9] rounded-md space-y-3 bg-gray-50/50">
                                {orderInventoryItems.map((item, index) => {
                                    const line = inventoryCostBreakdown.lines[index];
                                    const fullPrice = (item.unit_price ?? 0) * item.total_quantity;
                                    const includedQty = line?.includedQty ?? 0;
                                    const billableQty = line?.billableQty ?? item.total_quantity;
                                    const lineCost = line?.lineCost ?? fullPrice;
                                    const remainingAfterThis = line?.remainingAfterThis ?? 0;
                                    const isFullyIncluded = includedQty > 0 && billableQty === 0;
                                    const isPartialIncluded = includedQty > 0 && billableQty > 0;

                                    return (
                                    <div key={`order-item-${item.inventory_id || item.id || index}`}>
                                        <p className="text-gray-700 font-medium mb-1 text-sm font-raleway">
                                            {item.item_name}
                                            {item.unit_price !== undefined && (
                                                <span className="text-gray-500 font-normal"> · ${item.unit_price.toFixed(2)} each</span>
                                            )}
                                        </p>
                                        <div className="flex justify-between items-center bg-white p-3 rounded-md border-[1px] border-[#E9E9E9] font-open-sans gap-3">
                                            <div className="flex flex-col gap-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <label className="text-sm text-gray-600">Qty</label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        step={1}
                                                        disabled={!isBookingEditable}
                                                        value={item.total_quantity}
                                                        onChange={e => updateOrderItemQuantity(index, Number(e.target.value))}
                                                        className="w-20 p-2 border border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                {isFullyIncluded && (
                                                    <span className="text-xs text-primary-300 font-raleway">
                                                        {remainingAfterThis > 0
                                                            ? `${remainingAfterThis} included left`
                                                            : `${includedQty} included`}
                                                    </span>
                                                )}
                                                {isPartialIncluded && (
                                                    <span className="text-xs text-primary-300 font-raleway">
                                                        {includedQty} included / {billableQty} charged
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-800 font-semibold text-right">
                                                {isFullyIncluded ? (
                                                    <span className="flex flex-col items-end">
                                                        <span className="line-through text-gray-400 text-xs font-normal">
                                                            ${styleThousandCommas(fullPrice.toFixed(2))}
                                                        </span>
                                                        <span>$0.00</span>
                                                    </span>
                                                ) : (
                                                    `$${styleThousandCommas(lineCost.toFixed(2))}`
                                                )}
                                            </p>
                                            <button
                                                type="button"
                                                data-testid={`remove-order-item-${index}`}
                                                disabled={!isBookingEditable}
                                                onClick={() => removeOrderItem(index)}
                                                className="p-1 text-gray-500 hover:text-red-600"
                                            >
                                                <Close sx={{ fontSize: 20 }} />
                                            </button>
                                        </div>
                                    </div>
                                    );
                                })}
                                {isBookingEditable && catalogInventoryItems.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        <select
                                            data-testid="add-order-item-select"
                                            defaultValue=""
                                            onChange={e => {
                                                if (e.target.value) {
                                                    addOrderItem(e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                            className="w-full border-[1px] border-[#E9E9E9] bg-white rounded p-3 text-[16px] text-[#4D4D4D] font-open-sans"
                                        >
                                            <option disabled value="">Add an inventory item</option>
                                            {catalogInventoryItems
                                                .filter(catalogItem => !orderInventoryItems.some(
                                                    orderItem => orderItem.inventory_id === catalogItem.id
                                                ))
                                                .map(catalogItem => (
                                                    <option key={catalogItem.id} value={catalogItem.id}>
                                                        {catalogItem.item_name} - ${(catalogItem.unit_price ?? 0).toFixed(2)}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Booking Discounts - hidden for funding resource type or when total is 0 */}
                    {shouldShowFeesAndDiscounts && (additionalDiscounts.length > 0 || isBookingEditable) && (
                        <div>
                            <label className="font-medium text-gray-700">Booking Discounts</label>
                            <div className="mt-1 p-3 border-[2px] border-dashed border-[#E9E9E9] rounded-md space-y-3 bg-gray-50/50">
                                {additionalDiscounts.map((discount, index) => {
                                    if (!discount || typeof discount.rate !== 'number' || !discount.name || !checkDiscountActive(discount)) return null;
                                    return (
                                        <div key={index}>
                                            <p className="text-gray-700 font-medium mb-1 text-sm font-raleway">{discount.name} {discount.discount_type === 'percentage' ? `(${discount.rate.toFixed(0)}% Off)` : ``}</p>
                                            <div className="flex justify-between items-center bg-white p-3 rounded-md border-[1px] border-[#E9E9E9] font-open-sans">
                                                <p className="text-gray-800 font-semibold">{(discount.discount_type === 'percentage') ? `($${(discount.rate * (subtotal|| 0) / 100).toFixed(2)})` : (discount.rate && discount.discount_type === 'fixed') ? `($${discount.rate.toFixed(2)})` : 0}</p>
                                                <div className="flex items-center space-x-2">
                                                    <button onClick={() => removeDiscount(index)} disabled={!isBookingEditable} className="p-1 text-gray-500 hover:text-red-600"><Close sx={{ fontSize: 20 }} /></button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {isBookingEditable && (
                                    <button onClick={() => setIsOpenDiscount(true)} disabled={!isBookingEditable} className="flex items-center space-x-2 text-primary-300 font-semibold py-2 px-1">
                                        <span className="text-lg font-bold">+</span>
                                        <span>Add a Custom Discount</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {
                        (additionalNotes && additionalNotes.length > 0) && (
                            <div>
                                <label className="font-medium text-gray-700 font-raleway">Additional Notes</label>
                                <textarea 
                                    value={additionalNotes}
                                    disabled
                                    onChange={e => setAdditionalNotes(e.target.value)}
                                    className="mt-1 text-[16px] text-[#4D4D4D] font-open-sans block w-full p-3 border border-[#E9E9E9] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Add a note" 
                                />
                            </div>
                        )
                    }

                    <div className="p-4 border bg-gray-50 rounded-md space-y-2">
                        {shouldShowCostBreakdown && (
                            <>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 font-raleway font-medium">Subtotal</span>
                                    <span className="text-sm text-gray-900 font-open-sans">${styleThousandCommas(subtotal.toFixed(2))}</span>
                                </div>
                                {additionalFees.map((fee, index) => {
                                    if (!fee || typeof fee.rate !== 'number' || !fee.name) return null;
                                    return (
                                        <div key={`breakdown-fee-${index}`} className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500 font-raleway font-medium">{fee.name}</span>
                                            <span className="text-sm text-gray-700 font-open-sans">${styleThousandCommas(fee.rate.toFixed(2))}</span>
                                        </div>
                                    );
                                })}
                                {additionalDiscounts.map((discount, index) => {
                                    if (!discount || typeof discount.rate !== 'number' || !discount.name || !checkDiscountActive(discount)) return null;
                                    return (
                                        <div key={`breakdown-discount-${index}`} className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500 font-raleway font-medium">
                                                {discount.name} {discount.discount_type === 'percentage' ? `(${discount.rate.toFixed(0)}% Off)` : ''}
                                            </span>
                                            <span className="text-sm text-gray-700 font-open-sans">
                                                {(discount.discount_type === 'percentage')
                                                    ? `($${styleThousandCommas((discount.rate * (subtotal || 0) / 100).toFixed(2))})`
                                                    : (discount.rate && discount.discount_type === 'fixed')
                                                        ? `($${styleThousandCommas(discount.rate.toFixed(2))})`
                                                        : '$0.00'}
                                            </span>
                                        </div>
                                    );
                                })}
                                {orderInventoryItems.length > 0 && orderInventoryItems.map((item: OrderItemSchema, index: number) => {
                                    const line = inventoryCostBreakdown.lines[index];
                                    const fullPrice = (item.unit_price ?? 0) * item.total_quantity;
                                    const includedQty = line?.includedQty ?? 0;
                                    const billableQty = line?.billableQty ?? item.total_quantity;
                                    const lineCost = line?.lineCost ?? fullPrice;
                                    const remainingAfterThis = line?.remainingAfterThis ?? 0;
                                    const isFullyIncluded = includedQty > 0 && billableQty === 0;
                                    const isPartialIncluded = includedQty > 0 && billableQty > 0;

                                    return (
                                    <div key={`breakdown-inventory-${item.inventory_id || item.id || index}`} className="flex justify-between items-start">
                                        <div className="min-w-0">
                                            <span className="text-sm text-gray-500 font-raleway font-medium">
                                                {item.item_name} x{item.total_quantity}
                                            </span>
                                            {isFullyIncluded && (
                                                <p className="text-xs text-primary-300 font-raleway">
                                                    {remainingAfterThis > 0
                                                        ? `${remainingAfterThis} included left`
                                                        : `${includedQty} included`}
                                                </p>
                                            )}
                                            {isPartialIncluded && (
                                                <p className="text-xs text-primary-300 font-raleway">
                                                    {includedQty} included / {billableQty} charged
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-sm text-gray-700 font-open-sans text-right">
                                            {isFullyIncluded ? (
                                                <span className="flex flex-col items-end">
                                                    <span className="line-through text-gray-400 text-xs">
                                                        ${styleThousandCommas(fullPrice.toFixed(2))}
                                                    </span>
                                                    <span>$0.00</span>
                                                </span>
                                            ) : (
                                                `$${styleThousandCommas(lineCost.toFixed(2))}`
                                            )}
                                        </span>
                                    </div>
                                    );
                                })}
                            </>
                        )}
                        <div className="flex justify-between items-center pt-1 border-t border-gray-200">
                            <span className="font-bold text-gray-900">Total</span>
                            <span className="font-bold text-lg text-gray-900">${styleThousandCommas(total.toFixed(2))}</span>
                        </div>
                    </div>

                    {/* Payment Method - shown if payment method exists */}
                    {request.payment_method && (
                        <div className="p-4 rounded-md">
                            <div className="flex justify-between items-center">
                                <span className="font-bold text-gray-900">Payment Method</span>
                                <span className="font-bold text-sm text-gray-900 font-open-sans">
                                {
                                    (currentPaymethod && currentPaymethod.card) && (
                                        <div className='flex flex-row items-center'>
                                            {getCardIcon(currentPaymethod.card.brand)} &nbsp;
                                            <span className='text-primary-200 font-medium'>
                                                {currentPaymethod.type} ending in {currentPaymethod.card.last4 || '---'} &nbsp;
                                                <span className='text-gray-500 text-sm'>
                                                    (Exp: {currentPaymethod.card.exp_month}/{currentPaymethod.card.exp_year})
                                                </span>
                                            </span>
                                        </div>
                                    ) 
                                }
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end items-center space-x-3 pt-4 mt-2 border-t border-[#E9E9E9] flex-shrink-0 bg-white">
                    <button onClick={handleClose} className="px-5 py-2.5 rounded-md text-gray-700 font-semibold hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 mr-auto text-[16px]">Cancel</button>
                    <div className="flex items-center space-x-3 ml-auto">

                        <button 
                            onClick={() => setMessageOpen(true)} 
                            className="px-5 py-2.5 border border-[#E9E9E9] rounded-md text-gray-700 font-semibold hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 text-[16px]">
                            Send a Message
                        </button>

                        {
                            isBookingEditable && (
                                <button onClick={handleUpdateBooking} className="px-5 py-2.5 rounded-md bg-gray-800 text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 text-[16px]">Update {(request?.resource_type === 'kitchen') ? 'Booking' : "Request"} </button>
                            )
                        }
                    </div>
                </div>
            </div>

            {
                (isOpenFee && <AddFeeModal
                    resource={request}
                    isOpen={isOpenFee}
                    handleAddFee={handleAddFee}
                    closeAppModal={() => setIsOpenFee(false)}
                />
                )
            }

            {
            (request && 
            <AddDiscountModal
                isOpen={isOpenDiscount}
                currentResource={request}
                handleAddDiscount={handleAddDiscount}
                closeAppModal={() => setIsOpenDiscount(false)}
                />
            )
            }

           {
                (currentMember?.username && messageOpen) && (
                    <NewMessageModal 
                        username={currentMember?.username}
                        isOpen={messageOpen} 
                        closeAppModal={() => setMessageOpen(false)} 
                    />
                )
           }

           {selectedTeamMemberForDetails && viewTeamMemberDetails && ReactDOM.createPortal(
                <MemberDetailsModal
                    isOpen={viewTeamMemberDetails}
                    closeAppModal={() => {
                        setViewTeamMemberDetails(false);
                        setSelectedTeamMemberForDetails(null);
                    }}
                    member={selectedTeamMemberForDetails}
                />,
                document.body
           )}

           {showTeamMembersSection && (
                <BookingInviteTeamMemberModal
                    isOpen={isInviteTeamMemberModalOpen}
                    onClose={() => setIsInviteTeamMemberModalOpen(false)}
                    onInvite={handleInviteTeamMember}
                    existingMemberEmails={bookingTeamMembers.map((member) => member.email.trim().toLowerCase())}
                    lookupMemberships={allMemberships}
                />
           )}
        </>
    )
}

export default RequestDetails; 